import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FieldGroup, FieldLabel, FieldError } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

import { FilterSelect, DatePicker, SimpleSelect } from '@/shared/ui';

import { useStartingPoint, getStartingPointTypeOptions, type ConsignmentFormValues } from '../model';

interface StartingPointCardProps {
  form: UseFormReturn<ConsignmentFormValues>;
}

export function StartingPointCard({ form }: StartingPointCardProps) {
  const { t } = useTranslation();

  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors }
  } = form;

  const startingPointType = watch('startingPointType');

  const { locationOptions, isLoading, resetSearch, setSearchValue } = useStartingPoint({ startingPointType });

  const startingPointOptions = useMemo(() => getStartingPointTypeOptions(t), [t]);

  const handleStartingPointTypeChange = useCallback(
    (value: ConsignmentFormValues['startingPointType']) => {
      setValue('startingPointType', value);
      setValue('arrivingTo', '');
      resetSearch();
    },
    [resetSearch, setValue]
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground">{t('newcmr.arrivalident.label')}</CardTitle>
      </CardHeader>
      <CardContent>
        <FieldGroup>
          <Field>
            <Controller
              name="startingPointType"
              control={control}
              render={({ field }) => (
                <FilterSelect<ConsignmentFormValues['startingPointType']>
                  value={field.value}
                  onChange={handleStartingPointTypeChange}
                  options={startingPointOptions}
                  placeholder={t('validity.valueMissing.select')}
                  width="full"
                />
              )}
            />
            <FieldError errors={[errors.startingPointType]} />
          </Field>

          <Field className="flex flex-col gap-2">
            <Label className="text-sm font-medium">{t('newcmr.arrivingAt.label')} *</Label>
            <Controller
              name="arrivingTo"
              control={control}
              render={({ field }) => (
                <SimpleSelect
                  options={locationOptions}
                  value={field.value}
                  onChange={field.onChange}
                  onInputValueChange={setSearchValue}
                  placeholder={isLoading ? t('common.loading') : t('gen.autocomplete.title')}
                  emptyMessage={t('dashboard.locations.empty')}
                  className="w-full"
                />
              )}
            />
            <FieldError errors={[errors.arrivingTo]} />
          </Field>

          <Field>
            <Controller
              name="expectedArrivalDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label={t('newcmr.arrivalDate.label')}
                  value={field.value ? new Date(field.value) : null}
                  onChange={(date) => field.onChange(date?.toISOString())}
                  dateFormat="dd/MMM/yyyy"
                  id="expectedArrivalDate"
                />
              )}
            />
            <FieldError errors={[errors.expectedArrivalDate]} />
          </Field>

          <Field>
            <FieldLabel htmlFor="documentNo">{t('newcmr.docno.label')}</FieldLabel>
            <Input id="documentNo" {...register('documentNo')} placeholder="" />
            <FieldError errors={[errors.documentNo]} />
          </Field>

          <Field>
            <FieldLabel htmlFor="contactInfo">{t('request.arrivalContact.label')}</FieldLabel>
            <Input id="contactInfo" {...register('contactInfo')} placeholder="" />
            <FieldError errors={[errors.contactInfo]} />
          </Field>

          <Field>
            <FieldLabel htmlFor="instructions">{t('request.arrivalInstructions.label')}</FieldLabel>
            <Textarea id="instructions" {...register('instructions')} placeholder="" rows={3} />
            <FieldError errors={[errors.instructions]} />
          </Field>
        </FieldGroup>
      </CardContent>
    </Card>
  );
}
